import React, { useState, useEffect } from "react";
import { Eye, Trash2 } from "lucide-react";
import { Table, Button, Tag, Spin, Alert, Modal, message } from "antd";
import { getRequest, deleteRequest } from "../../../../service";
import ModalWrapper from "../../../../Modal";
import { getEmployeeId } from "../../../../utils/localStorage";
import { formatDate } from "../../../../utils/dateTime";
import LeaveRequest from "../../../../modal/admin/leaveRequestModal";

const Leave = () => {
  const [searchText, setSearchText] = useState("");
  const [leaveData, setLeaveData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const employeeId = getEmployeeId();

  const fetchLeaveRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getRequest(`/api/leaveRequest?employeeId=${employeeId}&onlyMe=true`);
      setLeaveData(res.leaveRequests || []);
    } catch (err) {
      setError("Failed to fetch leave requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeId) {
      fetchLeaveRequests();
    }
  }, [employeeId]);

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Delete Leave Request',
      content: 'Are you sure you want to delete this leave request? This action cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteRequest(`/api/leaveRequest?id=${record._id}`);
          message.success('Leave request deleted successfully');
          fetchLeaveRequests();
        } catch (err) {
          message.error('Failed to delete leave request');
        }
      }
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: "Sr",
      dataIndex: "_id",
      key: "_id",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      render: (date) => date ? formatDate(date) : "-",
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      render: (date) => date ? formatDate(date) : "-",
    },
    {
      title: "Leave Type",
      dataIndex: "leaveTypeName",
      key: "leaveTypeName",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status}
        </Tag>
      ),
    },
    {
      title: "Approved By",
      dataIndex: "approvedBy",
      key: "approvedBy",
      render: (_, record) =>
        record.approvedByInfo && record.approvedByInfo.name
          ? record.approvedByInfo.name
          : (record.approvedBy ? record.approvedBy : "-"),
    },
    {
      title: "Leave Days",
      dataIndex: "leaveDays",
      key: "leaveDays",
    },
    {
      title: "Actions ",
      key: "actions",
      render: (_, record) => (
        <div className="flex items-center gap-1">
          <Button
            type="text"
            icon={<Eye className="w-4 h-4 text-blue-600" />}
            onClick={() => {
              setViewData(record);
              setViewModalOpen(true);
            }}
          />
          <Button
            type="text"
            icon={<Trash2 className={`w-4 h-4 ${((record.status || '').toLowerCase() === 'pending') ? 'text-red-600' : 'text-gray-400'}`} />}
            onClick={() => ((record.status || '').toLowerCase() === 'pending') && handleDelete(record)}
            disabled={(record.status || '').toLowerCase() !== 'pending'}
            title={((record.status || '').toLowerCase() === 'pending') ? 'Delete request' : 'Only pending requests can be deleted'}
          />
        </div>
      ),
    },
  ];

  const filteredData = leaveData.filter((item) =>
    Object.values(item).some((value) =>
      value && value.toString().toLowerCase().includes(searchText.toLowerCase())
    )
  );

  return (
    <div className="w-full mx-auto p-4 md:p-6 space-y-4 md:space-y-6 bg-white rounded-lg">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">My Leave</h1>
      </div>
      {error && <Alert message={error} type="error" showIcon className="mb-4" />}
      <div className="w-full overflow-x-auto">
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="_id"
          scroll={{ x: 'max-content' }}
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
      </div>
      <ModalWrapper
        open={viewModalOpen}
        onRequestClose={() => {
          setViewModalOpen(false);
          setViewData(null);
        }}
        title="View Leave Request"
      >
        <LeaveRequest
          initialData={viewData && viewData.raw ? viewData.raw : viewData}
          onClose={() => {
            setViewModalOpen(false);
            setViewData(null);
          }}
          readOnly={true}
        />
      </ModalWrapper>
    </div>
  );
};

export default Leave;
